import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Navbar,
  LoadingSpinner,
  PartyPatronBanner,
  FeaturedStrip,
  AllEventsGrid,
} from '../components';
import StoriesRail from '../components/stories/StoriesRail';
import StoryViewer from '../components/stories/StoryViewer';
import PostEventFeedbackBanner from '../components/PostEventFeedbackBanner';
import RouteLoadError from '../components/transitions/RouteLoadError';
import squareService from '../services/squareService';
import useMyPortalSources from '../hooks/useMyPortalSources';
import useReelParam from '../hooks/useReelParam';
import { useUser, useAuth } from '../context';
import FilterDrawer from '../components/wall/FilterDrawer';
import useWallFilters from '../hooks/useWallFilters';
import useUserLocation from '../hooks/useUserLocation';
import { cameFromBack } from '../utils/backNav';
import { getWallCache, saveWallSnapshot, saveWallScroll, getWallScroll, saveWallAnchor, getWallAnchor } from '../utils/wallCache';
import useMediaQuery from '../utils/useMediaQuery';
import MobileEventSheet from '../components/MobileEventSheet';
import { peek } from '../data/routeData';
import { wallKey, loadWall as loadWallData } from '../data/routeLoaders';


/**
 * FeaturedWallPage — the events wall, /featured (events-wall handoff, Sept 2026).
 *
 *   Top bar (TopBar) · FILTERS pill + drawer · PORTALS rail (City Portal → live → SEND) ·
 *   PartyPatronBanner · FEATURED rail · ALL EVENTS masonry.
 *
 *   The "Feeling social?" card and its LIVE NOW strip are gone — City Portal and the live
 *   Portals are rings on the rail now. The "Showing parties near:" strip is gone too: narrowing
 *   lives in the Cities filter, and the location picker opens from there.
 *
 *   Filters (`useWallFilters`) live here so one selection narrows Featured and All Events alike.
 *   Category rides the URL (`?category=`) so it survives re-renders and direct links.
 *
 *   Location: from the browser's geolocation (asked once on first visit, remembered in
 *   localStorage) or picked by hand. The backend resolves coordinates to a region or ranks by
 *   distance; the user never sees regions.
 */
export default function FeaturedWallPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, logout } = useAuth();
  const { profile, walletBalance } = useUser();

  // Capture "did we arrive via browser back/forward" ONCE, at the page's first
  // render, while the popstate signal is still fresh. The AllEventsGrid mounts
  // only after the snapshot API responds (which can take a few seconds), so it
  // can't reliably re-check the time-based signal itself — we hand it this stable
  // value so a slow API can't make it miss the restore and reload only page 1.
  const cameFromBackRef = useRef(cameFromBack());

  // On back/forward, seed the snapshot from the local cache so the list renders
  // on the FIRST paint (no spinner, no API wait) — then loadWall refreshes it in
  // the background. On a fresh visit, start empty and load normally.
  const [snapshot, setSnapshot] = useState(() => (
    peek(wallKey(searchParams.get('category'))) || (cameFromBackRef.current ? (getWallCache()?.snapshot || null) : null)
  ));
  // Only "loading" (spinner/dim) when we have nothing cached to show yet.
  const [loading, setLoading] = useState(() => !snapshot);
  const [error, setError] = useState(null);
  const snapshotRef = useRef(snapshot);
  useEffect(() => { snapshotRef.current = snapshot; }, [snapshot]);

  // The city is the header's (top-header + location handoff, Oct 14 2026): one shared store,
  // set only by an explicit pick, read here as `{ lat, lng, label }` for the fetches that
  // narrow by map. Nothing on this page asks the browser where you are any more — the location
  // panel's USE MY CURRENT LOCATION is the only thing that does, and only when it is tapped.
  const browse = useUserLocation();
  const location = useMemo(
    () => (browse.lat != null && browse.lng != null ? { lat: browse.lat, lng: browse.lng, label: browse.city } : null),
    [browse.lat, browse.lng, browse.city],
  );

  const category = searchParams.get('category') || '';

  // Mobile: tapping an event opens it in a bottom sheet instead of navigating to
  // a new page. The open event rides a `?event=CODE` history entry, so the back
  // button closes the sheet (and forward re-opens it) without leaving the wall —
  // which also means the scroll position is never lost. Desktop keeps full-page
  // navigation.
  const isMobile = useMediaQuery('(max-width: 767px)');
  const openEventCode = searchParams.get('event');

  const handleEventOpen = useCallback((ev) => {
    const code = ev?.inviteCode;
    if (!code) return;
    if (isMobile) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('event', code);
        return next;
      });
    } else {
      navigate(`/events/${code}`);
    }
  }, [isMobile, navigate, setSearchParams]);

  // The story viewer rides its own history entry (?reel=), the same way the mobile event sheet
  // does, so the back button closes it and the wall's scroll position is never lost. A reload or
  // a shared link with ?reel= opens it too (useReelParam), from the rail's own reels.
  const [railReels, setRailReels] = useState([]);
  const { openReelUserId, reels: viewerReels, doorRect, openReel, closeReel, openNonce } = useReelParam({ railReels, location });

  const closeEventSheet = useCallback(() => {
    // Prefer a real history pop so the URL/back-stack stays clean; fall back to a
    // param removal if there's nothing to pop (e.g. opened via a direct link).
    const idx = window.history.state && typeof window.history.state.idx === 'number' ? window.history.state.idx : 0;
    if (idx > 0) {
      navigate(-1);
    } else {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('event');
        return next;
      }, { replace: true });
    }
  }, [navigate, setSearchParams]);

  // --- Scroll restoration (tap an event, hit back → land where you were) ---
  // FeaturedWallPage stays mounted the whole time you scroll, so it's the right
  // place to own scroll position (the drawer unmounts/remounts on refetch).
  // Save the window scroll continuously while on the wall.
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        saveWallScroll(window.scrollY);
        // The first card still on screen, and where it sat. A card's position is what the
        // restore is really after; the offset alone drifts whenever something above loads
        // at a different height.
        const cards = document.querySelectorAll('[data-event-id]');
        let anchor = null;
        for (const el of cards) {
          const r = el.getBoundingClientRect();
          if (r.bottom > 80) {
            if (!anchor || r.top < anchor.top) anchor = { id: el.getAttribute('data-event-id'), top: Math.round(r.top) };
            if (r.top > window.innerHeight) break;
          }
        }
        saveWallAnchor(anchor);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, []);

  // If we arrived via browser back/forward, restore the saved position. The list
  // content loads asynchronously (snapshot fetch + the drawer restoring its cached
  // pages), so poll on a timer (NOT rAF — rAF callbacks get starved right after a
  // history navigation commit) until the content has loaded, then land as close to
  // the saved spot as the restored content allows. Runs once on mount so a later
  // URL normalization can't cancel it.
  //
  // Then it HOLDS: for a few seconds the saved anchor card is pinned back to where it sat on
  // screen, because things above the list (the Portals rail, banners) and posters inside it
  // can still settle after the jump. The hold lets go the moment the user scrolls themselves.
  useEffect(() => {
    if (!cameFromBackRef.current) return undefined;
    const target = getWallScroll();
    if (target <= 0) return undefined;
    const anchor = getWallAnchor();
    let done = false;
    let holding = false;
    const timers = [];
    const startT = Date.now();
    let lastMax = -1;
    let stableSince = startT;
    const cardFor = () => (anchor?.id ? document.querySelector(`[data-event-id="${CSS.escape(anchor.id)}"]`) : null);
    const pin = () => {
      const el = cardFor();
      if (!el) return;
      const delta = el.getBoundingClientRect().top - anchor.top;
      if (Math.abs(delta) > 1) window.scrollBy(0, delta);
    };
    const release = () => { holding = false; };
    const inputs = ['wheel', 'touchstart', 'keydown', 'mousedown'];
    inputs.forEach((ev) => window.addEventListener(ev, release, { passive: true, once: true }));
    const hold = () => {
      if (!holding) return;
      pin();
      if (Date.now() - startT < 4000) timers.push(setTimeout(hold, 100));
    };
    const finish = (y) => {
      window.scrollTo(0, y);
      done = true;
      if (anchor) { holding = true; pin(); timers.push(setTimeout(hold, 100)); }
    };
    const attempt = () => {
      if (done) return;
      const now = Date.now();
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      // The page is tall enough to hold the exact saved position → land there.
      if (maxScroll >= target - 2) { finish(target); return; }
      // Track when the page height stops growing (content finished loading).
      if (maxScroll !== lastMax) { lastMax = maxScroll; stableSince = now; }
      // Content has loaded (taller than one viewport) but ended up a touch SHORTER
      // than the saved target — e.g. the user was at/near the bottom and a
      // banner/image differs between mounts. Once the height has settled, land at
      // the bottom (the closest we can get) rather than refusing to move.
      if (maxScroll > window.innerHeight && now - stableSince > 500) {
        finish(Math.min(target, maxScroll));
        return;
      }
      if (now - startT < 9000) timers.push(setTimeout(attempt, 100));
    };
    attempt();
    return () => {
      done = true;
      holding = false;
      timers.forEach(clearTimeout);
      inputs.forEach((ev) => window.removeEventListener(ev, release));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only the category param changes — ?event= and ?reel= ride the same URL.
  const onCategoryChange = useCallback((cat) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (cat) next.set('category', cat); else next.delete('category');
      return next;
    });
  }, [setSearchParams]);
  const cityPool = useMemo(() => [...(snapshot?.allEvents || []), ...(snapshot?.featured || [])], [snapshot]);
  const filters = useWallFilters({ restoreFromBack: cameFromBackRef.current, category, onCategoryChange, cityPool });
  const featured = useMemo(() => (snapshot?.featured || []).filter(filters.matches), [snapshot, filters.matches]);

  // The nearest town's square: its name is what NEAR says (never the browser's "Your
  // location"), and its headcount is the City Portal ring's tag.
  const [here, setHere] = useState(null);
  useEffect(() => {
    let cancelled = false;
    squareService.getHere({ lat: location?.lat ?? null, lng: location?.lng ?? null })
      .then((r) => { if (!cancelled && r?.success) setHere(r.data || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [location]);
  // Your live Portals opened from an event: that event's card reads PORTAL OPEN.
  const portalFor = useMyPortalSources();

  // The town the person PICKED is the answer; the region and the nearest square only fill in
  // before anything has been chosen.
  const nearLabel = browse.saved ? browse.city : (snapshot?.region?.name || here?.label || browse.city);

  const loadWall = useCallback(async () => {
    setError(null);
    // Show the spinner only when there's nothing already on screen — a back-nav
    // seeds from cache, so this becomes a silent background refresh.
    if (!snapshotRef.current) setLoading(true);
    try {
      const data = await loadWallData(category, undefined, location || {});
      setSnapshot(data);
      saveWallSnapshot(data);
    } catch (err) {
      if (!snapshotRef.current) setError(err.message);
    } finally { setLoading(false); }
  }, [category, location]);

  useEffect(() => { loadWall(); }, [loadWall]);

  return (
    <div className="min-h-screen">
      <Navbar
        user={profile}
        walletBalance={walletBalance}
        onLogout={isAuthenticated ? () => { logout(); navigate('/'); } : undefined}
        onLogoClick={() => navigate('/')}
      />

      <FilterDrawer filters={filters} location={location} locationLabel={nearLabel} />

      {/* PORTALS — loaded per viewer, never from the cached snapshot: who's on it and what
          you've watched are yours, the snapshot is shared. */}
      <StoriesRail location={location} onOpenReel={openReel} onLoaded={setRailReels} here={here} />

      <PartyPatronBanner patron={snapshot?.patron} />

      {/* Post-event "rate it" prompt — self-hides when nothing to rate. */}
      {isAuthenticated && <PostEventFeedbackBanner />}

      {error && (
        <RouteLoadError error={error} onRetry={loadWall} />
      )}

      {/* Featured + All Events stay mounted while we re-fetch, so nothing flickers. */}
      {(snapshot || loading) && !error && (
        <>
          {loading && !snapshot && <LoadingSpinner />}
          {snapshot && (
            <>
              <FeaturedStrip events={featured} onEventOpen={handleEventOpen} portalFor={portalFor} />
              <AllEventsGrid
                filters={filters}
                loading={loading}
                location={location}
                restoreFromBack={cameFromBackRef.current}
                onEventOpen={handleEventOpen}
                portalFor={portalFor}
              />
            </>
          )}
        </>
      )}

      {openReelUserId && viewerReels.length > 0 && (
        <StoryViewer
          // Keyed on the tap: every tap opens a fresh viewer, so one that failed to hand its
          // close back can never leave the ring dead.
          key={`${openReelUserId}:${openNonce}`}
          reels={viewerReels}
          startUserId={openReelUserId}
          doorRect={doorRect}
          onClose={closeReel}
        />
      )}

      {/* Mobile: single-event bottom sheet, opened by tapping a card. */}
      <MobileEventSheet
        code={openEventCode}
        open={isMobile && !!openEventCode}
        onClose={closeEventSheet}
      />
    </div>
  );
}

